import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { useInView } from "react-intersection-observer";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import useReportedContent from "../../hooks/useReportedContent";
import ReportedContentTable from "./components/ReportedContentTable";
import ModalMedia from "../../components/Modal/ModalMedia";
import {
  startApproveReportedContent,
  startRejectReportedContent,
} from "../../actions/owner";
import {
  ERROR_OCCURRED,
  OWNER_DASHBOARD_NAV_REPORTED_CONTENT,
} from "../../i18n/keysTranslations";
import { REPORTED_CONTENT_STATUSES } from "../../utils/constants";

const getModalMediaProps = ({ selectedContent } = {}) => {
  const urls = Array.isArray(selectedContent?.urls) ? selectedContent.urls : [];
  const hasMultipleUrls = urls.length > 1;
  const firstUrl = urls[0] || "";

  return {
    url: firstUrl,
    ...(hasMultipleUrls
      ? {
          isFromCarousel: true,
          item: {
            isCarousel: true,
            carouselImages: urls,
            _initialCarouselIndex: 0,
          },
        }
      : {
          isFromCarousel: false,
          item: null,
        }),
  };
};

const ReportedContentBrief = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const {
    rows,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    onUpdateRowStatus,
  } = useReportedContent();
  const [selectedContent, setSelectedContent] = useState(null);
  const modalMediaProps = getModalMediaProps({ selectedContent });
  const isSelectedContentPending =
    selectedContent?.status?.toLowerCase?.() ===
    REPORTED_CONTENT_STATUSES.PENDING;

  const { ref: lastItemRef, inView } = useInView({
    triggerOnce: false,
    rootMargin: "80px 0px",
  });

  useEffect(() => {
    if (!inView || !hasMore || loading || loadingMore) return;
    loadMore();
  }, [inView, hasMore, loading, loadingMore, loadMore]);

  const onViewContent = (row) => {
    setSelectedContent(row);
  };

  const onCloseModalMedia = () => {
    setSelectedContent(null);
  };

  const onApproveContent = async (row) => {
    const contentID = row?.key;
    if (!contentID) return;
    const response = await dispatch(
      startApproveReportedContent({
        contentID,
      })
    );
    if (response?.ok) {
      onUpdateRowStatus({
        contentID,
        status: "approved",
      });
    }
    return response;
  };

  const onRejectContent = async (row) => {
    const contentID = row?.key;
    if (!contentID) return;
    const response = await dispatch(
      startRejectReportedContent({
        contentID,
      })
    );
    if (response?.ok) {
      onUpdateRowStatus({
        contentID,
        status: "rejected",
      });
    }
    return response;
  };

  return (
    <>
      <ContainerPage hasMarginBottom={false}>
        <Helmet>
          <title>
            {`${theme.nameBusiness} - ${t(OWNER_DASHBOARD_NAV_REPORTED_CONTENT)}`}
          </title>
        </Helmet>
        <Box sx={{ width: "100%", px: 3, mt: 4, flexShrink: 0 }}>
          <TitlePage sx={{ mb: 2 }}>
            {t(OWNER_DASHBOARD_NAV_REPORTED_CONTENT)}
          </TitlePage>

          {error ? (
            <Typography color="error" sx={{ mb: 2 }}>
              {t(ERROR_OCCURRED)}
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ width: "100%", px: 3, mb: 4, minWidth: 0 }}>
          <ReportedContentTable
            rows={rows}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            lastItemRef={lastItemRef}
            onViewContent={onViewContent}
          />
        </Box>
      </ContainerPage>
      {selectedContent && (
        <ModalMedia
          modalOpen={!!selectedContent}
          onCloseModal={onCloseModalMedia}
          {...modalMediaProps}
          showEditButton={false}
          showDownloadButton={false}
          showShareButton={false}
          showDeleteButton={false}
          showApproveButton={isSelectedContentPending}
          showRejectButton={isSelectedContentPending}
          approveRejectData={selectedContent}
          onApproveContent={onApproveContent}
          onRejectContent={onRejectContent}
        />
      )}
    </>
  );
};

export default ReportedContentBrief;
